import React, { useMemo } from 'react';

import type { ModalProps as CrownModalProps } from 'types/opencare-crown';

import { CloseButton } from './components/CloseButton';
import { StyledModal } from './Modal-styled-components';

// These properties are hard-coded in the styled modal component and should never be passed in
type BlacklistedProp = 'closable' | 'onCancel' | 'footer' | 'modalRender';
// Duplicate this list for better static typing
const BLACKLISTED_PROPS: BlacklistedProp[] = [
  'closable',
  'onCancel',
  'footer',
  'modalRender',
];

interface ModalProps extends Omit<CrownModalProps, BlacklistedProp> {
  children: React.ReactNode;
  showCloseButton?: boolean;
  onClose: () => void;
}

/**
 * A wrapper around opencare-crown Modal which provides default styling to match the
 * design system. Simply provide the children and everything else is handled for you.
 *
 * For full prop documentation, see the Ant Design Modal component documentation.
 * {@link https://4x.ant.design/components/modal/?locale=en-US#API}
 */
export const Modal = ({
  children,
  showCloseButton = true,
  onClose,
  ...props
}: ModalProps) => {
  // We handle the close icon in our own way since we use a custom render
  const propsForStyledModal = useMemo(() => Object.fromEntries(
    Object.entries(props)
      .filter(([key]) => !BLACKLISTED_PROPS.includes(key as BlacklistedProp)),
  ), [props]);

  return (
    <StyledModal
      closable={showCloseButton}
      onCancel={onClose}
      {...propsForStyledModal}
    >
      {showCloseButton && (<CloseButton onClick={onClose} />)}
      {children}
    </StyledModal>
  );
};
